import React, { useEffect, useState } from "react";
import { MessageSquareText, CheckCircle2, RotateCcw, Image as ImageIcon } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, EmptyState, Modal } from "../lib/ui";
import { useToast } from "../lib/toast";
import { ROLE_LABELS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const TYPE_META = {
  BUG: { label: "Bug", tone: "danger" },
  IMPROVEMENT: { label: "Improvement", tone: "info" },
  MISSING_PRODUCT: { label: "Missing Product", tone: "warn" },
  WORKFLOW_ISSUE: { label: "Workflow Issue", tone: "warn" },
  OTHER: { label: "Other", tone: "mute" },
};

export default function Feedback() {
  const toast = useToast();
  const { isRole } = useAuth();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [shot, setShot] = useState(null);
  const canResolve = isRole("SUPER_ADMIN");

  const load = () => api.get("/feedback").then(({ data }) => setData(data));
  useEffect(() => { load();
    // eslint-disable-next-line
  }, []);
  if (!data) return <PageLoader />;

  const items = data.feedback.filter((f) => filter === "all" || f.status === filter);

  const resolve = async (f) => {
    try { await api.post(`/feedback/${f.id}/resolve`); toast.success(t("Status updated")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Feedback Review")} subtitle={`${data.open_count} ${t("open")} · ${data.total} ${t("total")}`} icon={MessageSquareText}>
        <div className="flex gap-2">
          {["all", "open", "resolved"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`fb-filter-${s}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold capitalize ${filter === s ? "bg-primary text-white" : "bg-white border border-line text-ink-soft"}`}>{t(s)}</button>
          ))}
        </div>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState title={t("No feedback yet")} subtitle={t("Branch feedback submitted during the pilot will appear here.")} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {items.map((f) => {
            const tm = TYPE_META[f.feedback_type] || { label: f.feedback_type, tone: "mute" };
            return (
              <Card key={f.id} className={`p-4 ${f.status === "resolved" ? "opacity-70" : ""}`} data-testid="feedback-item">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={tm.tone}>{t(tm.label)}</Badge>
                    <Badge tone={f.status === "resolved" ? "success" : "warn"}>{t(f.status)}</Badge>
                  </div>
                  {f.screenshot && (
                    <button onClick={() => setShot(f.screenshot)} className="text-ink-mute hover:text-primary" data-testid="fb-view-shot">
                      <ImageIcon size={18} />
                    </button>
                  )}
                </div>
                <p className="mt-2 text-sm text-ink">{f.notes}</p>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-3 text-xs text-ink-mute">
                  <span className="font-semibold text-ink-soft">{f.user_name}</span>
                  <span>{t(ROLE_LABELS[f.user_role] || f.user_role)}</span>
                  <span>· {f.branch_name || t("Central")}</span>
                  <span>· {f.page || "—"}</span>
                  <span>· {(f.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                {canResolve && (
                  <div className="mt-3 flex justify-end">
                    <Button variant={f.status === "resolved" ? "outline" : "success"} size="sm" onClick={() => resolve(f)} data-testid={`fb-resolve-${f.id}`}>
                      {f.status === "resolved" ? <><RotateCcw size={14} /> {t("Reopen")}</> : <><CheckCircle2 size={14} /> {t("Mark Resolved")}</>}
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={!!shot} onClose={() => setShot(null)} title={t("Screenshot")} size="lg">
        {shot && <img src={shot} alt="feedback screenshot" className="w-full rounded-lg" />}
      </Modal>
    </div>
  );
}
